import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

interface CarouselSlide {
  title: string;
  subtitle: string;
  categories: string[];
  image: string;
}

interface HeroCarouselProps {
  slides: CarouselSlide[];
  onBookNow: () => void;
}

export function HeroCarousel({ slides, onBookNow }: HeroCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative h-[600px] lg:h-[700px] overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <ImageWithFallback
            src={slides[currentIndex].image}
            alt={slides[currentIndex].title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
          
          {/* Decorative elements */}
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#d4a574]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>

          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="max-w-2xl text-white"
              >
                <Badge className="mb-4 bg-white/20 backdrop-blur-md text-white border-white/30 px-4 py-2">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {slides[currentIndex].subtitle}
                </Badge>
                
                <h1 className="text-5xl lg:text-7xl mb-6 text-white leading-tight">
                  {slides[currentIndex].title}
                </h1>
                
                <p className="text-white/90 text-lg mb-6 max-w-xl">
                  {slides[currentIndex].description}
                  <br></br>
                  {slides[currentIndex].currentPrice} <del>{slides[currentIndex].oldPrice}</del>
                </p>
                
                <div className="flex flex-wrap gap-3 mb-8">
                  {slides[currentIndex].categories.map((category, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className="px-5 py-2.5 bg-white/10 backdrop-blur-md rounded-full text-sm border border-white/20 hover:bg-white/20 transition-colors"
                    >
                      {category}
                    </motion.span>
                  ))}
                </div>
                
                <div className="flex gap-4">
                  <Button
                    onClick={onBookNow}
                    className="bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white rounded-full px-8 py-6 text-lg shadow-2xl shadow-[#d4a574]/50 transition-all duration-300 group"
                  >
                    Get Started Today
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  
                  {/* <Button
                    variant="outline"
                    className="bg-white/10 backdrop-blur-md text-white border-white/30 hover:bg-white/20 rounded-full px-8 py-6 text-lg transition-all duration-300"
                  >
                    Learn More
                  </Button> */}
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-10 w-14 h-14 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-all duration-300 group"
      >
        <ChevronLeft className="w-6 h-6 text-[#2c2c2c] group-hover:-translate-x-0.5 transition-transform" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-10 w-14 h-14 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-all duration-300 group"
      >
        <ChevronRight className="w-6 h-6 text-[#2c2c2c] group-hover:translate-x-0.5 transition-transform" />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2 bg-black/20 backdrop-blur-md px-4 py-3 rounded-full">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? "bg-white w-8" 
                : "bg-white/50 hover:bg-white/75 w-2"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-10">
        <motion.div
          key={currentIndex}
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 8, ease: "linear" }}
          className="h-full bg-gradient-to-r from-[#d4a574] to-white"
        />
      </div>
    </div>
  );
}
